import { isAppError } from '@/lib/api/errors';
import type { FieldError, PublishBlocker } from '@/lib/api/types';

/**
 * Helpers for the CMS-driven-collections governance gates (contract §4). The backend rejects
 * unsafe lifecycle transitions with structured field errors; these read them out so the editors
 * can give a non-technical user a clear, specific message instead of a generic "validation
 * error" — and know exactly which field to fix.
 */

function details(error: unknown): FieldError[] {
  return isAppError(error) ? (error.details ?? []) : [];
}

/** True when any field error carries the given backend code (e.g. `required_for_ready`). */
export function hasErrorCode(error: unknown, code: string): boolean {
  return details(error).some((d) => d.code === code);
}

/** Arabic copy gate (`locale_incomplete`) tripped on publish. */
export function isLocaleIncomplete(error: unknown): boolean {
  return hasErrorCode(error, 'locale_incomplete');
}

/**
 * The consent publish gate (`consent_pending` / `consent_missing`), which `be-svet` applies to a
 * collection declaring `RequiresConsent`. None does since D-76 (`svet_projects` and
 * `testimonials` made their consent reference optional); the branch stays because the backend
 * mechanism does, and a future gated collection must not fall through to the generic message.
 *
 * On such a collection the record saves fine, but its reference is still `pending:<id>` — the
 * sentinel that honestly records "no signed release exists yet" — so publishing is refused.
 *
 * It needs its own branch for the same reason the Arabic gate does, and more urgently. Falling
 * through to the generic "Please fix the highlighted fields" would point an editor at
 * `consent_ref`, which is the one field they must NOT edit their way past: the value is correct,
 * and the missing thing is a piece of paper. The backend's own message says so, so this surfaces
 * it verbatim rather than paraphrasing it.
 */
export function isConsentBlocked(error: unknown): boolean {
  return hasErrorCode(error, 'consent_pending') || hasErrorCode(error, 'consent_missing');
}

/** The backend's explanation of the consent refusal — what would unblock it, in its own words. */
export function consentBlockedDetail(error: unknown): string | undefined {
  return details(error).find((d) => d.code === 'consent_pending' || d.code === 'consent_missing')
    ?.message;
}

/** Field keys missing for a `ready` family (`required_for_ready`), e.g. `hero_image_id`. */
export function requiredForReadyFields(error: unknown): string[] {
  return details(error)
    .filter((d) => d.code === 'required_for_ready')
    .map((d) => d.field);
}

/** A reserved-slug rejection (`reserved`) — the slug shadows a system route. */
export function isReservedSlug(error: unknown): boolean {
  return hasErrorCode(error, 'reserved');
}

/**
 * Product slugs blocking a family delete (block-and-warn cascade, §3.4). The backend returns a
 * `409 conflict` listing them in the `products` field error; the message may be a single slug or
 * a comma/space-separated list, so we split defensively.
 */
export function blockingProductSlugs(error: unknown): string[] {
  if (!isAppError(error) || error.code !== 'conflict') return [];
  return details(error)
    .filter((d) => d.field === 'products' || d.field.startsWith('products'))
    .flatMap((d) => d.message.split(/[,\s]+/))
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * A refused publish, re-read as publish blockers.
 *
 * The item read already carries `publish_blockers` for content items and families, so on
 * those the notice is on screen before anyone presses Publish. This is the belt to that
 * braces, and it earns its place twice: it shows the refusal *immediately* rather than
 * after the detail query refetches, and it covers any gate the backend adds to a publish
 * without adding to the read. The field errors on a 422 are already the same
 * `{field, code, message}` triple, so nothing is paraphrased here either.
 */
export function publishBlockersFromError(error: unknown): PublishBlocker[] {
  return details(error).map((d) => ({ field: d.field, code: d.code, message: d.message }));
}

/**
 * The server's standing blockers plus anything the last refused publish added, deduped on
 * `code:field` with the server's copy winning — the server is the one that will actually
 * refuse, and a stale local copy of the same gate must never outrank it.
 */
export function mergeBlockers(
  fromRead: PublishBlocker[] | undefined,
  fromRefusal: PublishBlocker[],
): PublishBlocker[] {
  const out = [...(fromRead ?? [])];
  const seen = new Set(out.map((b) => `${b.code}:${b.field}`));
  for (const b of fromRefusal) {
    const k = `${b.code}:${b.field}`;
    if (!seen.has(k)) {
      seen.add(k);
      out.push(b);
    }
  }
  return out;
}
